import { ILongSteelOrderCache } from './types';
import {
  ELongSteelOrdersStatus,
  IBilledOrderData,
  IQCFileResponse,
  IDownloadNFEResponse,
  IOpenOrderData,
  SalesOrderItem,
  IOrderDivision,
  INewLongSteelOrdersResponse,
  ITrackingFlatSteel,
  IBilledOrderTracking,
  IFutureDelivery,
  ITrackingLongSteel,
  IUpdateOrderValidation,
  IUpdateOrderResponse,
  IOrdersChangeData,
  IOrdersChageDataResponse,
  IUpdateOrderValidationResponse,
  IUpdateOrderRequest,
  ICgCertificateRequest,
  CgCertificateStatus,
  ICgCertificateResponse,
  SalesOrderItemTable,
  Billing
} from '../../store/ducks/new-long-steel-orders/types';
import {
  customersAuthorizationApi,
  ECustomerAuthorizationEndpoints
} from '../api';
import { EGerdauService } from '../EGerdauService';
import { ELocalStorageKey } from '../EGerdauService/types';
import moment from 'moment';
import { ICustomer } from '../../store/ducks/customers/types';
import { ECustomerTypeView } from '../../store/ducks/user/types';
import { UnleashClient, IToggle } from '@unleash/proxy-client-react';
import { unleashProxyConfig } from '../../unleash-proxy';
import { getAllItems, addItem, cleanStore } from '../../utils/IndexedDB';
import { StoreNames } from '../../utils/IndexedDB/StoreNames';
import { downloadFile } from '../../store/ducks/new-long-steel-orders/sagas';
import { EOrderTab } from '../../store/ducks/long-steel-orders/types';

export class NewLongSteelOrdersService extends EGerdauService {
  readonly localStorageKey = ELocalStorageKey.LONG_STEEL_ORDERS;
  readonly unleash: UnleashClient;
  constructor() {
    super();
    this.unleash = new UnleashClient(unleashProxyConfig);
  }

  // chamar somente quando carregar
  async getOrdersFromCache(): Promise<INewLongSteelOrdersResponse> {
    const cacheData = await this.getOrdersFromClientSideCache();
    if (
      cacheData?.orders !== undefined &&
      new Date().getTime() - cacheData.datetime <= 3600000
    ) {
      return cacheData.orders;
    } else {
      return {
        items: [],
        receivers: [],
        updateItems: {
          downloadsItemsSelected: [],
          itemsSelected: [],
          itemsAllowed: [],
          itemsToUpdate: [],
          loading: {
            cancelOrders: false,
            downloadOrders: false,
            exportOrders: false,
            repeatOrders: false,
            validationOrders: false
          }
        },
        initialInputValues: {
          states: [],
          emitterIds: [],
          receiverId: '',
          startDate: undefined,
          endDate: undefined,
          orderStatus: ELongSteelOrdersStatus.ALL
        },
        divisions: [],
        lastLoadTime: undefined,
        orderDetail: []
      };
    }
  }

  async getOrdersFromClientSideCache(): Promise<ILongSteelOrderCache> {
    const orders = await getAllItems<ILongSteelOrderCache>(
      StoreNames.LongSteelOrders
    );

    return orders[0];
  }

  async getOrders(
    states: string[],
    customerIds: string[],
    customerViewType: ECustomerTypeView,
    startDate: string,
    endDate: string,
    isManager: boolean
  ): Promise<INewLongSteelOrdersResponse> {
    const headers = super.getHeaders();

    let response;
    const ordersResponse: INewLongSteelOrdersResponse = {
      items: [],
      receivers: [],
      divisions: [],
      lastLoadTime: new Date().getTime(),
      updateItems: {
        itemsSelected: [],
        itemsAllowed: [],
        itemsToUpdate: [],
        downloadsItemsSelected: [],
        loading: {
          cancelOrders: false,
          downloadOrders: false,
          exportOrders: false,
          repeatOrders: false,
          validationOrders: false
        }
      },
      initialInputValues: {
        states: states,
        emitterIds: [],
        receiverId: '',
        startDate,
        endDate,
        orderStatus: ELongSteelOrdersStatus.ALL
      },
      orderDetail: []
    };

    const requestDateFormat = 'YYYYMMDD';
    endDate = moment(endDate, 'YYYY-MM-DD').format(requestDateFormat);
    startDate = moment(startDate, 'YYYY-MM-DD').format(requestDateFormat);

    if (isManager) {
      const requestURL = ECustomerAuthorizationEndpoints.ORDERS_ISMANAGER;
      response = await customersAuthorizationApi.post(
        requestURL,
        { 'customers-id': customerIds, startDate, endDate },
        { headers }
      );
      ordersResponse.items = this.mergeArrayOfItems(
        response.data.items as SalesOrderItem[][]
      );
      ordersResponse.emitters = this.getEmitters(ordersResponse.items);
    }

    if (
      !isManager &&
      (customerViewType === ECustomerTypeView.RECEIVER ||
        customerViewType === ECustomerTypeView.FABRICATION_RECEIVER)
    ) {
      const requestURL = ECustomerAuthorizationEndpoints.SHIP_TO_ORDERS;
      response = await customersAuthorizationApi.post(
        requestURL,
        { 'customers-id': customerIds, startDate, endDate },
        { headers }
      );
      ordersResponse.items = this.mergeArrayOfItems(
        response.data.items as SalesOrderItem[][]
      );
      ordersResponse.emitters = this.getEmitters(ordersResponse.items);
    } else if (!isManager) {
      const requestURL = ECustomerAuthorizationEndpoints.SOLD_TO_ORDERS;
      response = await customersAuthorizationApi.post(
        requestURL,
        { 'customers-id': customerIds, startDate, endDate },
        { headers }
      );
      ordersResponse.items = this.mergeArrayOfItems(
        response.data.items as SalesOrderItem[][]
      );
      ordersResponse.receivers = this.getReceivers(ordersResponse.items);
    }

    this.setCanDownloadQC(ordersResponse.items as SalesOrderItem[]);
    this.setCanDownloadNF(ordersResponse.items as SalesOrderItem[]);
    ordersResponse.divisions = this.getDivisionsFromBilledOrders(
      ordersResponse.items as SalesOrderItem[]
    );
    ordersResponse.bpError = false;

    // Identificado casos em que o pedido após faturado perde, do objeto quantity, a informação de billing
    const orders = ordersResponse.items.map((item) => ({
      ...item,
      quantities: {
        ...item.quantities,
        ...(!item.quantities.billedQuantity && {
          billedQuantity: String(
            item?.billing?.reduce(
              (acc, itm) => acc + (Number(itm.billedQuantity) || 0),
              0
            ) || 0
          )
        }),
        ...(!item.quantities.pendingQuantity && {
          pendingQuantity: String(
            Math.max(
              (Number(item?.quantities?.requestedQuantity) || 0) -
                (item?.billing?.reduce(
                  (acc, itm) => acc + (Number(itm.billedQuantity) || 0),
                  0
                ) || 0) || 0,
              0
            )
          )
        }),
        ...(!item.quantities.billedConversionQuantity && {
          billedConversionQuantity: '0'
        })
      }
    }));

    this.startInitialInputValues(
      { ...ordersResponse, items: orders },
      states,
      customerIds,
      customerViewType,
      moment(startDate, requestDateFormat).format('YYYY-MM-DD'),
      moment(endDate, requestDateFormat).format('YYYY-MM-DD')
    );

    if (!this.doesOrderHasCGItem(ordersResponse.items)) {
      await this.setCachedOrders(ordersResponse, undefined);
    }

    return { ...ordersResponse, items: orders };
  }

  async getOrdersOpenOrBilled(
    states: string[],
    customerIds: string[],
    customerViewType: ECustomerTypeView,
    startDate: string,
    endDate: string,
    orderType: EOrderTab,
    isManager: boolean
  ): Promise<INewLongSteelOrdersResponse> {
    const headers = super.getHeaders();

    let response;
    const ordersResponse: INewLongSteelOrdersResponse = {
      items: [],
      receivers: [],
      divisions: [],
      lastLoadTime: new Date().getTime(),
      updateItems: {
        itemsSelected: [],
        itemsAllowed: [],
        itemsToUpdate: [],
        downloadsItemsSelected: [],
        loading: {
          cancelOrders: false,
          downloadOrders: false,
          exportOrders: false,
          repeatOrders: false,
          validationOrders: false
        }
      },
      initialInputValues: {
        states: states,
        emitterIds: [],
        receiverId: '',
        startDate,
        endDate,
        orderStatus: ELongSteelOrdersStatus.ALL
      },
      orderDetail: []
    };

    const requestDateFormat = 'YYYYMMDD';
    endDate = moment(endDate, 'YYYY-MM-DD').format(requestDateFormat);
    startDate = moment(startDate, 'YYYY-MM-DD').format(requestDateFormat);

    // if (isManager) {
    //   const requestURL = ECustomerAuthorizationEndpoints.ORDERS_ISMANAGER;
    //   response = await customersAuthorizationApi.post(
    //     requestURL,
    //     { 'customers-id': customerIds, startDate, endDate },
    //     { headers }
    //   );
    //   ordersResponse.items = this.mergeArrayOfItems(
    //     response.data.items as SalesOrderItem[][]
    //   );
    //   ordersResponse.emitters = this.getEmitters(ordersResponse.items);
    // }

    // if (
    //   !isManager &&
    //   (customerViewType === ECustomerTypeView.RECEIVER ||
    //     customerViewType === ECustomerTypeView.FABRICATION_RECEIVER)
    // ) {
    //   const requestURL = ECustomerAuthorizationEndpoints.SHIP_TO_ORDERS;
    //   response = await customersAuthorizationApi.post(
    //     requestURL,
    //     { 'customers-id': customerIds, startDate, endDate },
    //     { headers }
    //   );
    //   ordersResponse.items = this.mergeArrayOfItems(
    //     response.data.items as SalesOrderItem[][]
    //   );
    //   ordersResponse.emitters = this.getEmitters(ordersResponse.items);
    // } else if (!isManager) {
    //   const requestURL = ECustomerAuthorizationEndpoints.SOLD_TO_ORDERS;
    //   response = await customersAuthorizationApi.post(
    //     requestURL,
    //     { 'customers-id': customerIds, startDate, endDate },
    //     { headers }
    //   );
    //   ordersResponse.items = this.mergeArrayOfItems(
    //     response.data.items as SalesOrderItem[][]
    //   );
    //   ordersResponse.receivers = this.getReceivers(ordersResponse.items);
    // }

    if (orderType === EOrderTab.OPEN) {
      if (
        customerViewType === ECustomerTypeView.RECEIVER ||
        customerViewType === ECustomerTypeView.FABRICATION_RECEIVER
      ) {
        const requestURL = ECustomerAuthorizationEndpoints.RECEIVER_OPEN_ORDERS;
        response = await customersAuthorizationApi.post(
          requestURL,
          { 'customers-id': customerIds },
          { headers }
        );
        ordersResponse.items = this.mergeArrayOfItems(
          response.data.items as SalesOrderItem[][]
        );
        ordersResponse.emitters = this.getEmitters(ordersResponse.items);
      } else {
        const requestURL = ECustomerAuthorizationEndpoints.EMITTER_OPEN_ORDERS;
        response = await customersAuthorizationApi.post(
          requestURL,
          { 'customers-id': customerIds },
          { headers }
        );
        ordersResponse.items = this.mergeArrayOfItems(
          response.data.items as SalesOrderItem[][]
        );
        ordersResponse.receivers = this.getReceivers(ordersResponse.items);
      }
    } else {
      if (
        customerViewType === ECustomerTypeView.RECEIVER ||
        customerViewType === ECustomerTypeView.FABRICATION_RECEIVER
      ) {
        const requestURL =
          ECustomerAuthorizationEndpoints.RECEIVER_BILLED_ORDERS;
        response = await customersAuthorizationApi.post(
          requestURL,
          { 'customers-id': customerIds, startDate, endDate },
          { headers }
        );
        ordersResponse.items = this.mergeArrayOfItems(
          response.data.items as SalesOrderItem[][]
        );
        ordersResponse.emitters = this.getEmitters(ordersResponse.items);
      } else {
        const requestURL =
          ECustomerAuthorizationEndpoints.EMITTER_BILLED_ORDERS;
        response = await customersAuthorizationApi.post(
          requestURL,
          { 'customers-id': customerIds, startDate, endDate },
          { headers }
        );
        ordersResponse.items = this.mergeArrayOfItems(
          response.data.items as SalesOrderItem[][]
        );
        ordersResponse.receivers = this.getReceivers(ordersResponse.items);
      }
    }

    this.setCanDownloadQC(ordersResponse.items as SalesOrderItem[]);
    this.setCanDownloadNF(ordersResponse.items as SalesOrderItem[]);
    ordersResponse.divisions = this.getDivisionsFromBilledOrders(
      ordersResponse.items as SalesOrderItem[]
    );
    ordersResponse.bpError = false;

    // Identificado casos em que o pedido após faturado perde, do objeto quantity, a informação de billing
    const orders = ordersResponse.items.map((item) => ({
      ...item,
      quantities: {
        ...item.quantities,
        ...(!item.quantities.billedQuantity && {
          billedQuantity: String(
            item?.billing?.reduce(
              (acc, itm) => acc + (Number(itm.billedQuantity) || 0),
              0
            ) || 0
          )
        }),
        ...(!item.quantities.pendingQuantity && {
          pendingQuantity: String(
            Math.max(
              (Number(item?.quantities?.requestedQuantity) || 0) -
                (item?.billing?.reduce(
                  (acc, itm) => acc + (Number(itm.billedQuantity) || 0),
                  0
                ) || 0) || 0,
              0
            )
          )
        }),
        ...(!item.quantities.billedConversionQuantity && {
          billedConversionQuantity: '0'
        })
      }
    }));

    this.startInitialInputValues(
      { ...ordersResponse, items: orders },
      states,
      customerIds,
      customerViewType,
      moment(startDate, requestDateFormat).format('YYYY-MM-DD'),
      moment(endDate, requestDateFormat).format('YYYY-MM-DD')
    );

    if (!this.doesOrderHasCGItem(ordersResponse.items)) {
      await this.setCachedOrders(ordersResponse, undefined);
    }

    return { ...ordersResponse, items: orders };
  }

  async getOrderDetail(
    salesOrderId: string,
    customerId: string,
    customerViewType: ECustomerTypeView
  ): Promise<INewLongSteelOrdersResponse> {
    const headers = super.getHeaders();

    const ordersResponse: INewLongSteelOrdersResponse = {
      items: [],
      receivers: [],
      divisions: [],
      lastLoadTime: new Date().getTime(),
      updateItems: {
        itemsSelected: [],
        itemsAllowed: [],
        itemsToUpdate: [],
        downloadsItemsSelected: [],
        loading: {
          cancelOrders: false,
          downloadOrders: false,
          exportOrders: false,
          repeatOrders: false,
          validationOrders: false
        }
      },
      initialInputValues: {
        states: [],
        emitterIds: [],
        receiverId: '',
        startDate: '',
        endDate: '',
        orderStatus: ELongSteelOrdersStatus.ALL
      },
      orderDetail: []
    };

    // items/{ salesOrderId }?customer={ BP }&customerType={ customerType }
    const requestURL = `${ECustomerAuthorizationEndpoints.ORDERS_DETAIL}/${salesOrderId}`;
    const response = await customersAuthorizationApi.get(requestURL, {
      headers,
      params: {
        customer: customerId,
        customerType: customerViewType
      }
    });

    ordersResponse.items = this.mergeArrayOfItems(
      response.data.items as SalesOrderItem[][]
    );
    ordersResponse.emitters = this.getEmitters(ordersResponse.items);

    this.setCanDownloadQC(ordersResponse.items as SalesOrderItem[]);
    this.setCanDownloadNF(ordersResponse.items as SalesOrderItem[]);
    ordersResponse.divisions = this.getDivisionsFromBilledOrders(
      ordersResponse.items as SalesOrderItem[]
    );
    ordersResponse.bpError = false;

    // Identificado casos em que o pedido após faturado perde, do objeto quantity, a informação de billing
    const orders = ordersResponse.items.map((item) => ({
      ...item,
      quantities: {
        ...item.quantities,
        ...(!item.quantities.billedQuantity && {
          billedQuantity: String(
            item?.billing?.reduce(
              (acc, itm) => acc + (Number(itm.billedQuantity) || 0),
              0
            ) || 0
          )
        }),
        ...(!item.quantities.pendingQuantity && {
          pendingQuantity: String(
            Math.max(
              (Number(item?.quantities?.requestedQuantity) || 0) -
                (item?.billing?.reduce(
                  (acc, itm) => acc + (Number(itm.billedQuantity) || 0),
                  0
                ) || 0) || 0,
              0
            )
          )
        }),
        ...(!item.quantities.billedConversionQuantity && {
          billedConversionQuantity: '0'
        })
      }
    }));

    // this.startInitialInputValues(
    //   { ...ordersResponse, items: orders },
    //   states,
    //   customerIds,
    //   customerViewType,
    //   moment(startDate, requestDateFormat).format('YYYY-MM-DD'),
    //   moment(endDate, requestDateFormat).format('YYYY-MM-DD')
    // );

    // if (!this.doesOrderHasCGItem(ordersResponse.items)) {
    //   await this.setCachedOrders(ordersResponse, undefined);
    // }

    return { ...ordersResponse, items: orders };
  }

  private async setCachedOrders(
    orders: INewLongSteelOrdersResponse,
    customerId?: string
  ) {
    await this.cleanOrders();
    await addItem(StoreNames.LongSteelOrders, {
      orders,
      customerId,
      dateTime: new Date().getTime()
    });
  }

  async cleanOrders(): Promise<void> {
    await cleanStore(StoreNames.LongSteelOrders);
  }

  private mergeArrayOfItems(
    orderResponseItems: SalesOrderItem[][]
  ): SalesOrderItem[] {
    const items: SalesOrderItem[] = [];
    orderResponseItems.forEach((customerOrders) => {
      customerOrders.forEach((order) => {
        items.push(order);
      });
    });
    return items;
  }

  private doesOrderHasCGItem(orderResponseItems: SalesOrderItem[]) {
    for (const order of orderResponseItems) {
      const i = order as SalesOrderItem;
      if (!i.billing) {
        return false;
      }
      if (
        // i.billing.salesOrganization === 'BRCG' ||
        // i.billing.salesOrganization === 'BRGO'
        i.salesOrg === 'BRCG' ||
        i.salesOrg === 'BRGO'
      ) {
        return true;
      }
    }
    return false;
  }

  async getBilledOrderTracking(
    item: IBilledOrderData
  ): Promise<IBilledOrderTracking> {
    const headers = super.getHeaders();
    const requestURL = `${ECustomerAuthorizationEndpoints.BILLED_ORDER_TRACKING}/${item.nfeAccessKey}`;
    const response = await customersAuthorizationApi.get(requestURL, {
      headers
    });

    if (typeof response.data.invoiceAccessKey === 'string') {
      throw new Error(response.data.invoiceAccessKey);
    } else {
      return response.data.invoiceAccessKey as IBilledOrderTracking;
    }
  }

  // retorna os dados de tracking online
  async getTrackingFlatSteel(
    item: IOpenOrderData
  ): Promise<ITrackingFlatSteel> {
    const headers = super.getHeaders();
    const requestURL = `${ECustomerAuthorizationEndpoints.TRACKING_FLAT_STEEL}?salesOrder=${item.salesOrder}&itemNumber=${item.salesOrderItem}&division=${item.division}`;
    const response = await customersAuthorizationApi.get(requestURL, {
      headers
    });

    return response.data.length > 0
      ? (response.data[0] as ITrackingFlatSteel)
      : ({
          finish: 0,
          meltShop: 0,
          requestedQuantity: 0,
          rollingFinish: 0,
          rollingMill: 0,
          shipment: 0,
          stockWarehouse: 0,
          transit: 0,
          openQuantity: 0
        } as ITrackingFlatSteel);
  }

  // retorna dados de tracking D-1
  async getTrackingFlatSteelExcel(
    items: SalesOrderItemTable[],
    typeView: ECustomerTypeView
  ) {
    const headers = super.getHeaders();
    const requestURL = `${ECustomerAuthorizationEndpoints.TRACKING_FLAT_STEEL_EXCEL}`;

    const customers: string[] = [];
    const orders: {
      salesOrder: string;
      itemNumber: string;
      division: string;
    }[] = [];

    items.forEach((item) => {
      const itemCode =
        typeView === ECustomerTypeView.EMITTER
          ? item.soldTo.code
          : item.shipTo.code;
      if (!customers.includes(itemCode)) {
        customers.push(itemCode);
      }
      orders.push({
        salesOrder: item.salesOrder.toString(),
        itemNumber: item.salesOrderItem.toString(),
        division: item.division
      });
    });

    const bodyRequest = {
      customers,
      orders
    };
    const response = await customersAuthorizationApi.post(
      requestURL,
      bodyRequest,
      { headers }
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getItemTracking = (responseData: any, item: SalesOrderItemTable) => {
      const responseDataItemIdx = responseData.findIndex(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (rs: any) =>
          rs.salesOrder === item.salesOrder.toString() &&
          rs.itemNumber === item.salesOrderItem.toString()
      );

      if (responseDataItemIdx !== -1) {
        item.trackingFlatsteelExcel = responseData[responseDataItemIdx];
      } else {
        item.trackingFlatsteelExcel = {
          finish: 0,
          meltShop: 0,
          requestedQuantity: 0,
          rollingFinish: 0,
          rollingMill: 0,
          shipment: 0,
          stockWarehouse: 0,
          transit: 0,
          openQuantity: 0
        } as ITrackingFlatSteel;
      }
    };

    items.forEach((item: SalesOrderItemTable) => {
      getItemTracking(response.data.items, item);
    });
  }

  // retorna os dados de tracking online
  async getTrackingLongSteel(
    customerId: string,
    salesOrder: number,
    salesOrderItem: number
  ): Promise<ITrackingLongSteel> {
    const headers = super.getHeaders();
    const requestURL = `${ECustomerAuthorizationEndpoints.TRACKING_LONG_STEEL}?customer=${customerId}&salesOrderItem=${salesOrderItem}&salesOrder=${salesOrder}`;
    const response = await customersAuthorizationApi.get(requestURL, {
      headers
    });

    return response.data.tracking[0];
  }

  private sortOpenOrdersItemsByQuantities(items: IOpenOrderData[]) {
    const orderItemsByQuantitiesField = (
      sortedItems: IOpenOrderData[],
      field: string
    ): IOpenOrderData[] => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return sortedItems.sort((a: any, b: any) => {
        if (
          a.quantities[field] &&
          a.quantities[field] !== '' &&
          Number(a.quantities[field]) > 0 &&
          (!b.quantities[field] ||
            b.quantities[field] === '' ||
            Number(b.quantities[field]) === 0)
        ) {
          return -1;
        } else if (
          (!a.quantities[field] ||
            a.quantities[field] === '' ||
            Number(a.quantities[field]) === 0) &&
          b.quantities[field] &&
          b.quantities[field] !== '' &&
          Number(b.quantities[field]) > 0
        ) {
          return 1;
        } else {
          return 0;
        }
      });
    };

    // POR FATURADOS - billedQuantity
    items = orderItemsByQuantitiesField(items, 'billedQuantity');

    // POR REMESSA - deliveryQuantity
    items = orderItemsByQuantitiesField(items, 'deliveryQuantity');

    // POR CARREGAMENTO - shipmentQuantity
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    items = orderItemsByQuantitiesField(items, 'shipmentQuantity');
  }

  private sortFutureDeliveriesByEstimatedDeliveryDate(items: IOpenOrderData[]) {
    const getDateTime = (date: string): number => {
      const dateFormat = 'DD/MM/YYYY';
      let dateTime = 0;
      if (date.length > 10) {
        // caso retorne no formato "DD/MM/YYYY à DD/MM/YYYY"
        const splitedDate = date.split(' ');
        dateTime = moment(splitedDate[splitedDate.length - 1], dateFormat)
          .toDate()
          .getTime();
      } else {
        dateTime = moment(date, dateFormat).toDate().getTime();
      }
      return dateTime;
    };

    items.forEach((orderItem) => {
      orderItem.futureDeliveries.sort(
        (a: IFutureDelivery, b: IFutureDelivery) => {
          const dateA = a.estimatedDeliveryDate;
          const dateB = b.estimatedDeliveryDate;

          if (dateA && dateB) {
            return getDateTime(dateA) - getDateTime(dateB);
          } else if (dateA && !dateB) {
            return -1;
          } else if (!dateA && dateB) {
            return 1;
          } else {
            return 0;
          }
        }
      );
    });
  }

  private getReceivers(orders: SalesOrderItem[]): ICustomer[] {
    const receivers: ICustomer[] = [];

    orders.forEach((invoice) => {
      const isAlreadyAdd = receivers.find(
        (receiver) => receiver.customerId === invoice.shipTo.code
      );
      if (!isAlreadyAdd) {
        receivers.push({
          customerId: invoice.shipTo.code,
          name: invoice.shipTo.name,
          taxNumber: invoice.shipTo.taxNumber,
          locality: {
            address: invoice.shipTo.address,
            city: invoice.shipTo.city,
            district: invoice.shipTo.neighborhood,
            state: invoice.shipTo.state,
            country: invoice.shipTo.country,
            zipCode: invoice.shipTo.zipCode,
            zipcode: invoice.shipTo.zipCode
          },
          salesArea: [],
          division: []
        });
      }
    });

    return receivers;
  }

  private getEmitters(orders: SalesOrderItem[]): ICustomer[] {
    const emitters: ICustomer[] = [];

    orders.forEach((invoice) => {
      const isAlreadyAdd = emitters.find(
        (receiver) => receiver.customerId === invoice.soldTo.code
      );
      if (!isAlreadyAdd) {
        emitters.push({
          customerId: invoice.soldTo.code,
          name: invoice.soldTo.name,
          taxNumber: invoice.shipTo.taxNumber,
          locality: {
            address: invoice.soldTo.address,
            city: invoice.soldTo.city,
            district: invoice.soldTo.neighborhood,
            state: invoice.soldTo.state,
            country: invoice.soldTo.country,
            zipCode: invoice.soldTo.zipCode,
            zipcode: invoice.soldTo.zipCode
          },
          salesArea: [],
          division: []
        });
      }
    });

    return emitters;
  }

  private getDivisionsFromOpenOrders(
    orders: IOpenOrderData[]
  ): IOrderDivision[] {
    const divisions: IOrderDivision[] = [];
    orders.forEach((order) => {
      if (!divisions.find((div) => div.division === order.division)) {
        divisions.push({
          division: order.division,
          divisionDescription: order.divisionDescription
        });
      }
    });

    return divisions;
  }

  private getDivisionsFromBilledOrders(
    orders: SalesOrderItem[]
  ): IOrderDivision[] {
    const divisions: IOrderDivision[] = [];
    orders.forEach((order) => {
      if (
        order.billing &&
        !divisions.find((div) => div.division === order.division)
      ) {
        divisions.push({
          division: order.division,
          divisionDescription: order.divisionDescription
        });
      }
    });

    return divisions;
  }

  private setMinMaxDateInOpenOrders(
    ordersResponse: INewLongSteelOrdersResponse
  ) {
    // Ordena por data crescente
    (ordersResponse.items as SalesOrderItem[]).sort(
      (a, b) =>
        moment(a.requestedDate, 'YYYY-MM-DD').toDate().getTime() -
        moment(b.requestedDate, 'YYYY-MM-DD').toDate().getTime()
    );

    if (ordersResponse.items.length > 0) {
      ordersResponse.minOrderDate = (
        ordersResponse.items as SalesOrderItem[]
      )[0].requestedDate;
      ordersResponse.maxOrderDate = (ordersResponse.items as SalesOrderItem[])[
        ordersResponse.items.length - 1
      ].requestedDate;
    }
  }

  private setCanDownloadQC(orders: SalesOrderItem[]) {
    const canDownloadQC = ({
      item,
      loteAdminSKU
    }: {
      item: SalesOrderItem;
      loteAdminSKU: string;
    }): boolean => {
      if (!item || !loteAdminSKU) {
        return false;
      }
      const plant = item.plant || item.billingHistory.plant;
      // const plant = '22';
      const division = item.division || item.billingHistory.division; //item.division
      // const salesOrganization = item.billing.salesOrganization;
      const salesOrganization =
        item.salesOrg || item.billingHistory.salesOrganization;
      const adminSku = loteAdminSKU;
      const brcgPlantsNotAllowed = ['24'];
      const plantsNotAllowed = [
        '60',
        '62',
        '63',
        '64',
        '69',
        '70',
        '71',
        '73',
        '74',
        '75'
      ];
      if (
        !this.unleash.isEnabled(
          `${process.env?.REACT_APP_BASE_URL}_cg_certificado_qualidade`
        )
      ) {
        plantsNotAllowed.push('44', '345');
      }
      //debbuger;
      if ('BRCG' == salesOrganization) {
        for (const na of brcgPlantsNotAllowed) {
          if (division == na) {
            return false;
          }
        }
      }

      for (const ap of plantsNotAllowed) {
        if (plant.startsWith(ap) || adminSku !== 'X') {
          return false;
        }
      }

      if (brcgPlantsNotAllowed.includes(plant.slice(0, 2))) {
        return false;
      }
      return true;
    };

    orders.forEach((item) => {
      if (item.billing) {
        item.billing = item.billing.map((billing) => ({
          ...billing,
          canDownloadQC: canDownloadQC({
            item,
            loteAdminSKU: billing?.loteAdminSKU
          })
        }));
        return;
      }

      if (item.billingHistory) {
        item.billingHistory = {
          ...item.billingHistory,
          canDownloadQC: canDownloadQC({
            item,
            loteAdminSKU: item?.billingHistory?.loteAdminSKU
          })
        };
      }
    });
  }

  private readonly setCanDownloadNF = (orders: SalesOrderItem[]) => {
    const unavailableText = 'Indisponível';

    const canDownloadNF = ({ billing }: { billing: Billing }) => {
      return (
        billing.unitPriceWithoutTaxes !== unavailableText &&
        billing.invoiceItemValueWithoutTaxes !== unavailableText &&
        billing.invoiceItemValueWithTaxes !== unavailableText &&
        billing.invoiceTotalValueWithoutTaxes !== unavailableText &&
        billing.invoiceTotalValueWithTaxes !== unavailableText
      );
    };

    orders.forEach((item) => {
      if (item.billing) {
        item.billing = item.billing.map((billing) => ({
          ...billing,
          canDownloadNF: canDownloadNF({ billing })
        }));

        return;
      }

      if (item.invoices) {
        item.billing = item?.invoices?.map((billing) => ({
          ...billing,
          canDownloadNF: canDownloadNF({ billing })
        }));

        return;
      }

      if (item.billingHistory) {
        item.billingHistory = {
          ...item.billingHistory,
          canDownloadNF:
            item.conditions.unitPriceWithoutTaxes !== unavailableText &&
            item.conditions.invoiceItemValueWithoutTaxes !== unavailableText &&
            item.conditions.invoiceItemValueWithTaxes !== unavailableText &&
            item.conditions.invoiceTotalValueWithoutTaxes !== unavailableText &&
            item.conditions.invoiceTotalValueWithTaxes !== unavailableText
        };
      }
    });
  };

  private setNewCanDownloadQC = ({
    order,
    billing
  }: {
    order: SalesOrderItem;
    billing: Billing;
  }) => {
    const canDownloadQC = ({
      item,
      billing
    }: {
      item: SalesOrderItem;
      billing: Billing;
    }): boolean => {
      if (!item || !billing) {
        return false;
      }
      const plant = item.plant;
      // const plant = '22';
      const division = item.division; //item.division
      // const salesOrganization = item.billing.salesOrganization;
      const salesOrganization = item.salesOrg;
      const adminSku = billing.loteAdminSKU;
      const brcgPlantsNotAllowed = ['24'];
      const plantsNotAllowed = [
        '60',
        '62',
        '63',
        '64',
        '69',
        '70',
        '71',
        '73',
        '74',
        '75'
      ];

      if (
        !this.unleash.isEnabled(
          `${process.env?.REACT_APP_BASE_URL}_cg_certificado_qualidade`
        )
      ) {
        plantsNotAllowed.push('44', '345');
      }
      //debbuger;
      if ('BRCG' == salesOrganization) {
        for (const na of brcgPlantsNotAllowed) {
          if (division == na) {
            return false;
          }
        }
      }

      for (const ap of plantsNotAllowed) {
        if (plant.startsWith(ap) || adminSku !== 'X') {
          return false;
        }
      }

      if (brcgPlantsNotAllowed.includes(plant.slice(0, 2))) {
        return false;
      }
      return true;
    };

    return canDownloadQC({ item: order, billing });
  };

  private readonly setNewCanDownloadNF = ({
    billing
  }: {
    billing: Billing;
  }) => {
    const unavailableText = 'Indisponível';

    const canDownloadNF = ({ billing }: { billing: Billing }) => {
      return (
        billing.unitPriceWithoutTaxes !== unavailableText &&
        billing.invoiceItemValueWithoutTaxes !== unavailableText &&
        billing.invoiceItemValueWithTaxes !== unavailableText &&
        billing.invoiceTotalValueWithoutTaxes !== unavailableText &&
        billing.invoiceTotalValueWithTaxes !== unavailableText
      );
    };

    return canDownloadNF({ billing });
  };

  private startInitialInputValues(
    ordersResponse: INewLongSteelOrdersResponse,
    states: string[],
    customerIds: string[],
    customerViewType: ECustomerTypeView,
    startDate: string,
    endDate: string
  ) {
    this.setMinMaxDateInOpenOrders(ordersResponse);

    const selectedEmitter =
      customerViewType === ECustomerTypeView.EMITTER
        ? customerIds
        : ordersResponse.emitters && ordersResponse.emitters.length === 1
        ? [ordersResponse.emitters[0].customerId]
        : [];

    const selectedReceiver =
      customerViewType === ECustomerTypeView.EMITTER
        ? ordersResponse.receivers && ordersResponse.receivers.length === 1
          ? ordersResponse.receivers[0].customerId
          : ''
        : customerIds[0];

    ordersResponse.initialInputValues = {
      states: states,
      emitterIds: selectedEmitter,
      receiverId: selectedReceiver,
      orderStatus: ELongSteelOrdersStatus.ALL,
      startDate: ordersResponse.minOrderDate
        ? ordersResponse.minOrderDate
        : startDate,
      endDate: ordersResponse.maxOrderDate
        ? ordersResponse.maxOrderDate
        : endDate
    };
  }

  async loadNFEFile(docNum: string): Promise<IDownloadNFEResponse> {
    const headers = new EGerdauService().getHeaders();
    const response = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.GENERATE_BILLING}?docNum=${docNum}&businessType=long-steel`,
      { headers }
    );

    return response.data as IDownloadNFEResponse;
  }

  async loadBulkNFEFile(billings: Billing[]): Promise<IDownloadNFEResponse[]> {
    const headers = new EGerdauService().getHeaders();

    const promises = billings.map(async (item) => {
      const response = await customersAuthorizationApi.get(
        `${ECustomerAuthorizationEndpoints.GENERATE_BILLING}?docNum=${item.danfeNumber}&businessType=long-steel`,
        { headers }
      );

      if (response.data && response.data.FILE) {
        downloadFile({ billings: [item], nfe: response.data });
      }
      return response.data;
    });

    const allResponses = await Promise.all(promises);

    return allResponses;
  }

  async loadShippingNoteFile(
    deliveryDocumentZvoi: string
  ): Promise<IDownloadNFEResponse> {
    const headers = new EGerdauService().getHeaders();
    const response = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.GENERATE_BILLING}?deliveryDocument=${deliveryDocumentZvoi}&businessType=long-steel`,
      { headers }
    );

    return response.data as IDownloadNFEResponse;
  }

  async loadQCFile(billings: Billing[]): Promise<IQCFileResponse[]> {
    const headers = new EGerdauService().getHeaders();

    const promises = billings.map(async (item) => {
      const downloadQCresponse = await customersAuthorizationApi.get(
        `${ECustomerAuthorizationEndpoints.DOWNLOAD_CQ_FILE}?qcNumber=${
          item.cqNumber
        }&deliveryNumber=${
          item.deliveryCode
        }&deliveryItemNumber=${item.deliveryItemCode.replace(/^0+/, '')}`,
        { headers }
      );

      const qcFileResponse = downloadQCresponse.data as IQCFileResponse;

      if (
        !qcFileResponse.qcFile.qcBase64File ||
        qcFileResponse.qcFile.qcBase64File === 'Certificado não encontrado'
      ) {
        const generateCQresponse = await customersAuthorizationApi.get(
          `${ECustomerAuthorizationEndpoints.GENERATE_CQ}?delivery=${
            item.deliveryCode
          }&item=${item.deliveryItemCode.replace(/^0+/, '')}`,
          { headers }
        );

        qcFileResponse.qcFile.qcBase64File = generateCQresponse.data.FILE;
      }

      if (qcFileResponse && qcFileResponse.qcFile) {
        downloadFile({ billings: [item], qc: qcFileResponse });
      }

      return qcFileResponse;
    });

    const allResponses = await Promise.all(promises);

    return allResponses;
  }

  async downloadMultipleCQs(
    orders: SalesOrderItem[]
  ): Promise<IQCFileResponse> {
    const headers = new EGerdauService().getHeaders();

    const billings: Billing[] = orders.reduce((acc: Billing[], item) => {
      if (item.billing) {
        return [...acc, ...item.billing];
      }

      return acc;
    }, []);

    const payload = billings.map((order) => {
      return {
        qcNumber: order.cqNumber,
        deliveryNumber: order.deliveryCode,
        deliveryItemNumber: order.deliveryItemCode
      };
    });
    const response = await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.MULTIPLE_CQ,
      [...payload],
      { headers }
    );
    return response.data as IQCFileResponse;
  }

  static async DownloadReportOrders(date: string): Promise<string> {
    const headers = new EGerdauService().getHeaders();
    const downloadCQresponse = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.REPORT_ORDERS_DOWNLOAD}/${date}`,
      { headers }
    );

    const base64File: string = downloadCQresponse.data;
    return base64File;
  }

  async validationToUpdate(
    orders: IUpdateOrderValidation[]
  ): Promise<IUpdateOrderValidationResponse[]> {
    const headers = new EGerdauService().getHeaders();
    const response = await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.VALIDATE_ORDERS_TO_UPDATE,
      orders,
      { headers }
    );
    return response.data;
  }

  getNewOrderDetail = async ({
    orderId,
    customerId,
    customerType
  }: {
    orderId: string;
    customerId: string;
    customerType: ECustomerTypeView;
  }): Promise<SalesOrderItem[]> => {
    const headers = new EGerdauService().getHeaders();
    const response = await customersAuthorizationApi.get(
      ECustomerAuthorizationEndpoints.ORDERS_DETAIL + orderId,
      {
        headers,
        params: {
          customer: customerId,
          customerType
        }
      }
    );

    const itemsOrders = response.data.items as SalesOrderItem[];

    const items: SalesOrderItem[] = itemsOrders.map((item) => ({
      ...item,
      ...(item.billing &&
        Array.isArray(item.billing) &&
        item.billing.length > 0 && {
          billing: item.billing.map((billing) => ({
            ...billing,
            canDownloadQC: this.setNewCanDownloadQC({ order: item, billing }),
            canDownloadNF: this.setNewCanDownloadNF({ billing })
          }))
        })
    }));

    return items;
  };

  async changeOrderData(
    orders: IOrdersChangeData[] //SalesOrderItem[]
  ): Promise<IOrdersChageDataResponse[]> {
    const headers = new EGerdauService().getHeaders();
    const response = await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.ORDER_CHANGE_DATA,
      orders,
      { headers }
    );
    return response.data;
  }

  async sendOrderUpdateData(
    orders: IUpdateOrderRequest[]
  ): Promise<IUpdateOrderResponse[]> {
    const headers = new EGerdauService().getHeaders();
    const response = await customersAuthorizationApi.patch(
      ECustomerAuthorizationEndpoints.EDIT_ITEMS,
      { items: orders },
      { headers }
    );
    return response.data.items;
  }

  async solicitateCgCertificate(
    item: IBilledOrderData
  ): Promise<ICgCertificateResponse> {
    const headers = new EGerdauService().getHeaders();
    const body: ICgCertificateRequest = {
      code: item.soldTo.code,
      deliveryCode: item.deliveryCode,
      deliveryItemCode: item.deliveryItemCode,
      invoiceNumber: item.invoiceNumber,
      plant: item.billing.plant,
      salesOrder: item.billing.salesOrder.toString().padStart(10, '0'),
      salesOrderItem: item.billing.salesOrderItem.toString().padStart(6, '0'),
      sku: item.billing.sku.toString(),
      statusQC: item.billing.statusQC ?? CgCertificateStatus.Available,
      product: item.billing.materialDescription,
      productionBatch: item.productionBatch
    };
    const response = await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.DELIVERY_QC,
      [body],
      { headers }
    );
    return response.data;
  }
}
